import { useState } from "preact/hooks"
import useSWR from "swr"
import { format, parseISO } from "date-fns"
import { History } from "lucide-react"
import { Button } from "../../../components/ui/button"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../../components/ui/dialog"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../../components/ui/alert-dialog"
import { toast } from "../../../components/ui/use-toast"
import * as ForecastAPI from "../api/forecast_api"

interface ForecastChangelogDialogProps {
  version_id: string | null
  className?: string
  /**
   * Asked BEFORE the restore request fires (e.g. unsaved-edits confirmation).
   * Returning false aborts the revert with the server untouched.
   */
  confirm_before_restore?: () => Promise<boolean>
  /** Called after a successful revert with the restored (current) forecast version. */
  on_restored?: (restored_version: ForecastAPI.ApiForecastVersion) => void | Promise<void>
}

export interface SaveContextFromPayload {
  driver?: string
  notes?: string
}

/** Parses `payload.save_context` from a forecast audit row (hierarchy save metadata). */
export function extract_save_context_from_audit_payload(payload: unknown): SaveContextFromPayload {
  if (payload == null || typeof payload !== "object") {
    return {}
  }
  const raw = (payload as Record<string, unknown>).save_context
  if (raw == null || typeof raw !== "object") {
    return {}
  }
  const o = raw as Record<string, unknown>
  const driver = typeof o.driver === "string" ? o.driver : undefined
  const notes = typeof o.notes === "string" ? o.notes : undefined
  return { driver, notes }
}

/** One customer a hierarchy save moved, from the audit payload's `changes` (written by the server). */
export interface HierarchyCustomerChange {
  customer: string
  kind: "changed" | "added" | "removed"
  delta: number
}

/** The per-customer changes a save recorded, largest first as the server sorted them; empty when it recorded none. */
export function extract_changes_from_audit_payload(payload: unknown): HierarchyCustomerChange[] {
  if (payload == null || typeof payload !== "object") {
    return []
  }
  const raw = (payload as Record<string, unknown>).changes
  if (!Array.isArray(raw)) {
    return []
  }
  const out: HierarchyCustomerChange[] = []
  for (const item of raw) {
    if (item == null || typeof item !== "object") continue
    const o = item as Record<string, unknown>
    const customer = typeof o.customer === "string" ? o.customer : null
    const delta = Number(o.delta)
    const kind = o.kind === "added" || o.kind === "removed" ? o.kind : "changed"
    if (customer == null || !Number.isFinite(delta)) continue
    out.push({ customer, kind, delta })
  }
  return out
}

/** Short human label for the changelog entry being reverted (used in confirm copy). */
export function revert_target_label(row: ForecastAPI.ForecastAuditEventRow): string {
  const sc = extract_save_context_from_audit_payload(row.payload)
  let when = ""
  try {
    when = format(parseISO(row.created_at), "MMM d, yyyy HH:mm")
  } catch {
    when = "this date"
  }
  if (sc.driver?.trim()) {
    const d = sc.driver.trim()
    const short = d.length > 42 ? `${d.slice(0, 39)}…` : d
    return `${short} (${when})`
  }
  return when
}

function format_delta_currency(value: string | null | undefined): string {
  if (value == null || value === "") {
    return "—"
  }
  const n = Number(value)
  if (!Number.isFinite(n)) {
    return String(value)
  }
  // A drop reads as a drop: the save dialog already prints "−", the changelog did not.
  const sign = n > 0 ? "+" : n < 0 ? "−" : ""
  return `${sign}$${Math.abs(n).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`
}

function format_who(row: ForecastAPI.ForecastAuditEventRow): { primary: string; secondary?: string } {
  const name = row.actor_name?.trim()
  const email = row.actor_email?.trim()
  if (name && email) {
    return { primary: name, secondary: email }
  }
  if (name) {
    return { primary: name }
  }
  if (email) {
    return { primary: email }
  }
  if (row.actor_id) {
    return { primary: row.actor_id }
  }
  return { primary: "—" }
}

export function ForecastChangelogDialog(props: ForecastChangelogDialogProps) {
  const [open, setOpen] = useState(false)
  const [restoring_id, setRestoringId] = useState<string | null>(null)
  const [pending_revert_row, set_pending_revert_row] = useState<ForecastAPI.ForecastAuditEventRow | null>(null)
  const { data: rows, error, isLoading, mutate } = useSWR(
    open && props.version_id ? ["forecast-changelog", props.version_id] : null,
    () => ForecastAPI.listForecastChangelog(props.version_id!)
  )

  function open_revert_confirm(row: ForecastAPI.ForecastAuditEventRow): void {
    if (row.revert_available !== true) {
      return
    }
    set_pending_revert_row(row)
  }

  async function confirm_revert(): Promise<void> {
    const row = pending_revert_row
    if (row == null || row.revert_available !== true) {
      return
    }
    set_pending_revert_row(null)
    // Unsaved-edits check must precede the restore call: once the server has
    // reverted, cancelling would leave stale edits over already-reverted state.
    if (props.confirm_before_restore && !(await props.confirm_before_restore())) {
      return
    }
    setRestoringId(row.id)
    try {
      const restored_version = await ForecastAPI.restoreForecastAuditEvent(row.id)
      await props.on_restored?.(restored_version)
      await mutate()
      toast({
        title: "Forecast reverted",
        description: `${restored_version.name} was restored to the state before ${revert_target_label(row)}. You can revert this revert from the changelog.`,
      })
    } catch (e) {
      console.error(e)
      toast({
        title: "Restore failed",
        description: e instanceof Error ? e.message : "Could not restore this changelog entry.",
        variant: "destructive",
      })
    } finally {
      setRestoringId(null)
    }
  }

  return (
    <>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className={props.className}
        disabled={!props.version_id}
        onClick={() => setOpen(true)}
      >
        <History className="h-4 w-4 mr-2" />
        Changelog
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[80vh] max-w-5xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Forecast changelog</DialogTitle>
          </DialogHeader>
          {isLoading ? (
            <p className="text-sm text-gray-500">Loading…</p>
          ) : null}
          {error ? (
            <p className="text-sm text-red-600">Failed to load changelog.</p>
          ) : null}
          {!isLoading && rows && rows.length === 0 ? (
            <p className="text-sm text-gray-500">No audit events yet.</p>
          ) : null}
          {rows && rows.length > 0 ? (
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-left text-gray-600">
                  <th className="py-2 pr-3 font-medium">When</th>
                  <th className="py-2 pr-3 font-medium">Who</th>
                  <th className="py-2 pr-3 font-medium">Channel</th>
                  <th className="py-2 pr-3 font-medium">Event</th>
                  <th className="py-2 pr-3 font-medium">Forecast Δ</th>
                  <th className="py-2 pr-3 font-medium">Actual Δ</th>
                  <th className="py-2 pr-3 font-medium">Driver / notes</th>
                  <th className="py-2 pr-3 font-medium">Summary</th>
                  <th className="py-2 font-medium">Revert</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row: ForecastAPI.ForecastAuditEventRow) => {
                  const who = format_who(row)
                  const sc = extract_save_context_from_audit_payload(row.payload)
                  const changes = extract_changes_from_audit_payload(row.payload)
                  return (
                    <tr key={row.id} className="border-b border-gray-100 align-top">
                      <td className="py-2 pr-3 whitespace-nowrap text-gray-800">
                        {(() => {
                          try {
                            return format(parseISO(row.created_at), "MMM d, yyyy HH:mm")
                          } catch {
                            return row.created_at
                          }
                        })()}
                      </td>
                      <td className="py-2 pr-3 text-gray-800">
                        <div className="font-medium">{who.primary}</div>
                        {who.secondary ? <div className="text-xs text-gray-500">{who.secondary}</div> : null}
                      </td>
                      <td className="py-2 pr-3 text-gray-700">{row.change_channel ?? "—"}</td>
                      <td className="py-2 pr-3 text-gray-700">{row.event_type}</td>
                      <td className="py-2 pr-3 font-mono text-xs">
                        {format_delta_currency(row.forecast_volume_delta ?? undefined)}
                      </td>
                      <td className="py-2 pr-3 font-mono text-xs">
                        {format_delta_currency(row.actual_volume_delta ?? undefined)}
                      </td>
                      <td className="py-2 pr-3 text-gray-800">
                        {sc.driver || sc.notes ? (
                          <div className="space-y-1">
                            {sc.driver ? <div className="font-medium">{sc.driver}</div> : null}
                            {sc.notes ? (
                              <div className="text-xs whitespace-pre-wrap text-gray-600">{sc.notes}</div>
                            ) : null}
                          </div>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                      <td className="py-2 pr-3 text-gray-800">
                        {changes.length > 0 ? (
                          <ul className="space-y-0.5">
                            {changes.map((c: HierarchyCustomerChange) => (
                              <li key={`${c.kind}-${c.customer}`} className="flex items-baseline gap-2">
                                <span className="font-medium">{c.customer}</span>
                                {c.kind !== "changed" ? <span className="text-xs text-gray-500">{c.kind}</span> : null}
                                <span className="font-mono text-xs">{format_delta_currency(String(c.delta))}</span>
                              </li>
                            ))}
                          </ul>
                        ) : (
                          row.summary
                        )}
                      </td>
                      <td className="py-2 align-top">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          disabled={
                            row.revert_available !== true || restoring_id != null
                          }
                          title={
                            row.revert_available === true
                              ? "Restore this forecast to exactly how it was immediately before this change. You can always revert the revert."
                              : "Cannot revert this row (no saved state before it, or it predates reliable snapshots)"
                          }
                          onClick={() => open_revert_confirm(row)}
                        >
                          {restoring_id === row.id ? "…" : "Revert"}
                        </Button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          ) : null}
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={pending_revert_row != null}
        onOpenChange={(next: boolean) => {
          if (!next) {
            set_pending_revert_row(null)
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revert this forecast?</AlertDialogTitle>
            <AlertDialogDescription className="space-y-3 text-left text-sm text-gray-700">
              <span className="block">
                This forecast will be restored to exactly how it was <strong>immediately before</strong>
                {pending_revert_row ? <> “{revert_target_label(pending_revert_row)}”</> : " this change"}. Every row,
                breakdown, and setting comes back from the saved snapshot.
              </span>
              <span className="block">
                The revert is recorded in the changelog, so you can always revert the revert from there.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel type="button">Cancel</AlertDialogCancel>
            <AlertDialogAction type="button" onClick={() => void confirm_revert()}>
              Revert forecast
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest

  describe("extract_save_context_from_audit_payload", () => {
    it("returns empty when payload missing", () => {
      expect(extract_save_context_from_audit_payload(undefined)).toEqual({})
      expect(extract_save_context_from_audit_payload(null)).toEqual({})
    })

    it("reads driver and notes from save_context", () => {
      expect(
        extract_save_context_from_audit_payload({
          save_context: { driver: "Promo shift", notes: "Line A" },
        })
      ).toEqual({ driver: "Promo shift", notes: "Line A" })
    })
  })

  describe("extract_changes_from_audit_payload", () => {
    it("reads the server's per-customer changes and drops malformed rows", () => {
      const changes = extract_changes_from_audit_payload({
        changes: [
          { customer: "Walmart", kind: "added", delta: "9000.00" },
          { customer: "Whole Foods", kind: "changed", delta: -200 },
          { customer: 7, delta: 1 },
        ],
      })
      expect(changes).toEqual([
        { customer: "Walmart", kind: "added", delta: 9000 },
        { customer: "Whole Foods", kind: "changed", delta: -200 },
      ])
      expect(extract_changes_from_audit_payload({ save_context: {} })).toEqual([])
    })
  })

  describe("revert_target_label", () => {
    it("uses driver and formatted date when present", () => {
      const label = revert_target_label({
        id: "x",
        version_id: "v",
        org_id: "o",
        event_type: "hierarchy_saved",
        summary: "Saved",
        created_at: "2026-05-08T18:23:00.000Z",
        payload: { save_context: { driver: "UNFI West expansion", notes: "" } },
      } as ForecastAPI.ForecastAuditEventRow)
      expect(label).toContain("UNFI West expansion")
      expect(label).toMatch(/May/)
    })

    it("falls back to the date when no driver is present", () => {
      const label = revert_target_label({
        id: "x",
        version_id: "v",
        org_id: "o",
        event_type: "hierarchy_saved",
        summary: "Saved",
        created_at: "2026-05-08T18:23:00.000Z",
        payload: null,
      } as ForecastAPI.ForecastAuditEventRow)
      expect(label).toMatch(/May/)
    })
  })
}
